// apps/web/src/pages/admin-users-page.tsx — Fase 01, Plano 16 (USR-02/USR-03); Fase 06 (CRUD completo).
import { useState, useEffect, type ReactElement } from 'react';
import { UserTable, type UserRow } from '../components/users/user-table';
import { UserDialog } from '../components/users/user-dialog';
import { UserDeactivateDialog } from '../components/users/user-deactivate-dialog';
import { UserPinDialog } from '../components/users/user-pin-dialog';
import { apiMutate } from '../lib/csrf-fetch';

export function AdminUsersPage(): ReactElement {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [deactivateUser, setDeactivateUser] = useState<UserRow | null>(null);
  const [pinUser, setPinUser] = useState<UserRow | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [resetLink, setResetLink] = useState<{ nome: string; link: string } | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/users');
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error('Acesso negado. Apenas administradores podem gerenciar usuários.');
        }
        throw new Error('Falha ao carregar lista de usuários.');
      }
      const data = (await res.json()) as { users: UserRow[] };
      setUsers(data.users ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  const handleResetLink = async (user: UserRow): Promise<void> => {
    setActionMsg(null);
    try {
      const res = await apiMutate(`/api/v1/users/${user.id}/reset-senha`, { method: 'POST' });
      if (!res.ok) {
        const p = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(p.message ?? 'Não foi possível gerar o link de senha.');
      }
      const data = (await res.json()) as { setupLink: string };
      setResetLink({ nome: user.displayName || user.username, link: data.setupLink });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar link de senha.');
    }
  };

  const handleReactivate = async (user: UserRow): Promise<void> => {
    setActionMsg(null);
    setError(null);
    try {
      const res = await apiMutate(`/api/v1/users/${user.id}/reactivate`, { method: 'POST' });
      if (!res.ok) {
        const p = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(p.message ?? 'Não foi possível reativar o usuário.');
      }
      setActionMsg(`Usuário ${user.displayName || user.username} reativado.`);
      void fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao reativar usuário.');
    }
  };

  return (
    <div className="admin-users-page min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestão de Usuários e Permissões</h1>
            <p className="text-sm text-slate-500 mt-1">Administração de contas, perfis e controle de acesso granular</p>
          </div>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded text-sm hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            + Novo Usuário
          </button>
        </header>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-medium" role="alert">
            {error}
          </div>
        )}
        {actionMsg && (
          <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm font-medium" role="status">
            {actionMsg}
          </div>
        )}

        <main className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <UserTable
            users={users}
            loading={loading}
            onRefresh={() => void fetchUsers()}
            onEditRequested={(u) => setEditUser(u)}
            onResetLinkRequested={(u) => void handleResetLink(u)}
            onReactivateRequested={(u) => void handleReactivate(u)}
            onDeactivateRequested={(u) => setDeactivateUser(u)}
            onDefinirPinRequested={(u) => setPinUser(u)}
          />
        </main>
      </div>

      <UserDialog
        open={dialogOpen || Boolean(editUser)}
        editUser={editUser}
        onClose={() => {
          setDialogOpen(false);
          setEditUser(null);
        }}
        onUserCreated={() => void fetchUsers()}
      />

      {deactivateUser && (
        <UserDeactivateDialog
          isOpen={!!deactivateUser}
          userId={deactivateUser.id}
          userName={deactivateUser.displayName || deactivateUser.username}
          onClose={() => setDeactivateUser(null)}
          onDeactivated={() => void fetchUsers()}
        />
      )}

      {pinUser && (
        <UserPinDialog
          aberto={Boolean(pinUser)}
          usuario={pinUser}
          onFechar={() => setPinUser(null)}
          onSucesso={(msg) => {
            setActionMsg(msg);
            void fetchUsers();
          }}
        />
      )}

      {resetLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-md shadow-xl space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Link de definição de senha</h2>
            <p className="text-sm text-slate-600">
              Novo link (uso único, 24h) para <strong>{resetLink.nome}</strong>. Copie e envie ao usuário:
            </p>
            <div className="p-2 bg-slate-100 rounded font-mono text-xs break-all select-all text-slate-800">
              {resetLink.link}
            </div>
            <button
              type="button"
              onClick={() => setResetLink(null)}
              className="w-full py-2 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700"
            >
              Concluir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
